import { useEffect, useMemo, useState } from "react";

import { apiRequest, ensureCsrfToken } from "../../../api";

import "../../../css/TemplateSettingView.css";

import Swal from "sweetalert2";

function buildMappingRows(headers, savedMapping = {}, suggestions = {}) {
  return headers.map((headerName) => {
    const saved = savedMapping?.[headerName];
    const suggestion = suggestions?.[headerName];

    // --------------------------------------------------
    // Check whether an ACTUAL mapping exists
    // Empty string, null, undefined, "-", etc. = unmapped
    // --------------------------------------------------
    const savedType = String(saved?.type ?? "").trim();
    const savedField = String(saved?.field ?? "").trim();

    const hasSavedMapping = Boolean(savedType && savedField);

    // --------------------------------------------------
    // CASE 1: Actual saved mapping exists
    // --------------------------------------------------
    if (hasSavedMapping) {
      return {
        headerName,

        mandatory: saved?.mandatory ?? false,

        mappingType: savedType,
        mappedField: savedField,

        isSaved: true,
        mappingStatus: "mapped",

        // Keep suggestions so user can switch type later
        suggestions: suggestion || null,
      };
    }

    // --------------------------------------------------
    // CASE 2: No actual mapping, but suggestion exists
    // --------------------------------------------------
    if (suggestion?.type && suggestion?.header) {
      return {
        headerName,

        mandatory: saved?.mandatory ?? false,

        // Show best suggestion initially
        mappingType: suggestion.type,
        mappedField: suggestion.header,

        isSaved: false,
        mappingStatus: "suggestion",

        // Keep ALL suggestions:
        // system / po / grn
        suggestions: suggestion,
      };
    }

    // --------------------------------------------------
    // CASE 3: No mapping and no suggestion
    // --------------------------------------------------
    return {
      headerName,

      mandatory: saved?.mandatory ?? false,

      mappingType: "",
      mappedField: "",

      isSaved: false,
      mappingStatus: "unmapped",

      suggestions: suggestion || null,
    };
  });
}

function createTemplateOptions(fileCount) {
  const options = [];
  if (fileCount >= 1) {
    options.push({ key: "templateheader1", label: "Template Header 1" });
  }
  if (fileCount >= 2) {
    options.push({ key: "templateheader2", label: "Template Header 2" });
  }
  return options;
}

export default function TemplateSettingView() {
  const [context, setContext] = useState(null);
  const [activeTab, setActiveTab] = useState("upload");
  const [templateMode, setTemplateMode] = useState("common");
  const [uploadTemplateFor, setUploadTemplateFor] = useState("material");
  const [mappingTemplateFor, setMappingTemplateFor] = useState("material");
  const [fileCount, setFileCount] = useState("");
  const [uploadFiles, setUploadFiles] = useState({});
  const [uploadStatus, setUploadStatus] = useState("");
  const [mappingStatus, setMappingStatus] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("templateheader1");
  const [mappingRows, setMappingRows] = useState([]);

  const mappedFieldOptions = useMemo(() => {
    if (!context) {
      return {};
    }

    return {
      system: context.system_vars || [],
      po: context.po_headers || [],
      grn: context.grn_headers || [],
    };
  }, [context]);

  const currentMappingType = templateMode === "separate" ? mappingTemplateFor : "common";

  const currentMappingFileCount = useMemo(() => {
    if (!context) {
      return 0;
    }
    if (currentMappingType === "material") {
      return context.material_file_no || 0;
    }
    if (currentMappingType === "service") {
      return context.service_file_no || 0;
    }
    return context.file_no || 0;
  }, [context, currentMappingType]);

  const templateOptions = useMemo(
    () => createTemplateOptions(currentMappingFileCount),
    [currentMappingFileCount],
  );

  const hasMappedTemplates = templateOptions.length > 0;

  function getTemplateHeaders(nextContext, scope, templateKey) {
    if (scope === "material") {
      return templateKey === "templateheader1"
        ? nextContext.material_templateheader1 || []
        : nextContext.material_templateheader2 || [];
    }
    if (scope === "service") {
      return templateKey === "templateheader1"
        ? nextContext.service_templateheader1 || []
        : nextContext.service_templateheader2 || [];
    }
    return templateKey === "templateheader1"
      ? nextContext.templateheader1 || []
      : nextContext.templateheader2 || [];
  }

  function getSavedTemplateMapping(nextContext, scope, templateKey) {
    if (scope === "material") {
      return templateKey === "templateheader1"
        ? nextContext.material_templateMapping1 || {}
        : nextContext.material_templateMapping2 || {};
    }
    if (scope === "service") {
      return templateKey === "templateheader1"
        ? nextContext.service_templateMapping1 || {}
        : nextContext.service_templateMapping2 || {};
    }
    return templateKey === "templateheader1"
      ? nextContext.templateMapping1 || {}
      : nextContext.templateMapping2 || {};
  }

  function getTemplateSuggestions(nextContext, scope, templateKey) {
    if (scope === "material") {
      return templateKey === "templateheader1"
        ? nextContext.material_mapped_headers1_suggestions || {}
        : nextContext.material_mapped_headers2_suggestions || {};
    }

    if (scope === "service") {
      return templateKey === "templateheader1"
        ? nextContext.service_mapped_headers1_suggestions || {}
        : nextContext.service_mapped_headers2_suggestions || {};
    }

    return templateKey === "templateheader1"
      ? nextContext.mapped_headers1_suggestions || {}
      : nextContext.mapped_headers2_suggestions || {};
  }

  

  useEffect(() => {
    let cancelled = false;

    async function loadContext() {
      try {
        setError("");
        const response = await apiRequest("/template-setting/context/");
        if (cancelled) {
          return;
        }

        const nextContext = response.data;
        setContext(nextContext);
        setTemplateMode(nextContext.is_separate_template ? "separate" : "common");
        syncTemplateSelection(nextContext, "templateheader1", mappingTemplateFor);
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || "Unable to load template setting context.");
        }
      }
    }

    loadContext();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!context) {
      return;
    }

    const scope = context.is_separate_template ? mappingTemplateFor : "common";
    const options = createTemplateOptions(
      scope === "material"
        ? context.material_file_no || 0
        : scope === "service"
          ? context.service_file_no || 0
          : context.file_no || 0,
    );
    const nextSelected =
      options.find((option) => option.key === selectedTemplate)?.key || options[0]?.key || "templateheader1";

    if (nextSelected !== selectedTemplate) {
      setSelectedTemplate(nextSelected);
      return;
    }

    setMappingRows(
      buildMappingRows(
        getTemplateHeaders(context, scope, nextSelected),
        getSavedTemplateMapping(context, scope, nextSelected),
        getTemplateSuggestions(context, scope, nextSelected),
      ),
    );
  }, [context, mappingTemplateFor, selectedTemplate]);

  function syncTemplateSelection(nextContext, preferredTemplate, preferredScope) {
    const scope = nextContext.is_separate_template ? preferredScope : "common";
    const fileTotal =
      scope === "material"
        ? nextContext.material_file_no || 0
        : scope === "service"
          ? nextContext.service_file_no || 0
          : nextContext.file_no || 0;
    const options = createTemplateOptions(fileTotal);
    const selected =
      options.find((option) => option.key === preferredTemplate)?.key || options[0]?.key || "templateheader1";

    const nextSelected =
      options.find((option) => option.key === selectedTemplate)?.key || options[0]?.key || "templateheader1";

    setSelectedTemplate(selected);
    setMappingRows(
      buildMappingRows(
        getTemplateHeaders(context, scope, nextSelected),
        getSavedTemplateMapping(context, scope, nextSelected),
        getTemplateSuggestions(context, scope, nextSelected),
      ),
    );
  }

  function handleFileCountChange(value) {
    setFileCount(value);
    const count = Number(value || 0);
    const next = {};
    for (let index = 1; index <= count; index += 1) {
      next[`file_${index}`] = {
        file: null,
        headerRows: "1",
      };
    }
    setUploadFiles(next);
  }

  function updateUploadFile(key, field, value) {
    setUploadFiles((current) => ({
      ...current,
      [key]: {
        ...current[key],
        [field]: value,
      },
    }));
  }

  function updateRow(index, field, value) {
    setMappingRows((current) =>
      current.map((row, rowIndex) => {
        if (rowIndex !== index) {
          return row;
        }

        // ---------------------------------------------
        // Normal field change
        // ---------------------------------------------
        if (field !== "mappingType") {
          return {
            ...row,
            [field]: value,

            // User manually changed mapped field,
            // so it is no longer just a suggestion.
            ...(field === "mappedField"
              ? {
                  mappingStatus: "mapped",
                }
              : {}),
          };
        }

        // ---------------------------------------------
        // Mapping type changed
        // ---------------------------------------------
        const selectedType = value;

        const typeSuggestion =
          row.suggestions?.[selectedType];

        const suggestedHeader =
          typeSuggestion?.header || "";

        return {
          ...row,

          mappingType: selectedType,

          // Automatically use suggestion for selected type
          mappedField: suggestedHeader,

          // If suggestion exists for this type,
          // mark it as suggestion.
          mappingStatus: typeSuggestion
            ? "suggestion"
            : "unmapped",
        };
      })
    );
  }

  function confirmTemplateSuggestion(index) {
    setMappingRows((current) =>
      current.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              mappingStatus: "mapped",
            }
          : row
      )
    );
  }

  async function reloadContext(preferredTemplate = selectedTemplate, preferredScope = mappingTemplateFor) {
    const response = await apiRequest("/template-setting/context/");
    const nextContext = response.data;
    setContext(nextContext);
    setTemplateMode(nextContext.is_separate_template ? "separate" : "common");
    syncTemplateSelection(nextContext, preferredTemplate, preferredScope);
  }

  async function handleTemplateUpload() {
    if (!fileCount) {
      await Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Select number of files first.",
      });
      return;
    }

    setUploading(true);
    setError("");
    setNotice("");

    try {
      const csrfToken = await ensureCsrfToken();
      const formData = new FormData();

      const count = Number(fileCount);
      const isSeparate = templateMode === "separate";
      const templateFor = isSeparate ? uploadTemplateFor : "common";

      formData.append("template_for", templateFor);
      formData.append("is_separate", String(isSeparate));

      for (let index = 1; index <= count; index += 1) {
        const key = `file_${index}`;
        const currentFile = uploadFiles[key]?.file;

        if (!currentFile) {
          throw new Error(`Please upload File ${index}.`);
        }

        formData.append(key, currentFile);
        formData.append(
          `${key}_header_rows`,
          uploadFiles[key]?.headerRows || "1"
        );
      }

      const response = await fetch("/api/template-setting/upload/", {
        method: "POST",
        credentials: "include",
        headers: {
          "X-CSRFToken": csrfToken,
        },
        body: formData,
      });

      const payload = await response.json();

      if (!response.ok || payload.status !== "success") {
        throw new Error(
          payload.error || "Unable to upload template headers."
        );
      }

      setFileCount("");
      setUploadFiles({});

      await reloadContext(
        count === 2 ? "templateheader2" : "templateheader1",
        templateFor
      );

      await Swal.fire({
        icon: "success",
        title: "Upload Successful",
        text: "Base template uploaded successfully.",
        confirmButtonText: "OK",
      });

    } catch (requestError) {

      await Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text:
          requestError.message ||
          "Unable to upload template headers.",
        confirmButtonText: "OK",
      });

      setError(
        requestError.message ||
        "Unable to upload template headers."
      );

    } finally {
      setUploading(false);
    }
  }

  async function handleMappingSave() {
    setSaving(true);
    setError("");
    setNotice("");

    try {
      const csrfToken = await ensureCsrfToken();
      const mapping = {};

      mappingRows.forEach((row) => {
        if (row.headerName) {
          mapping[row.headerName] = {
            mandatory: row.mandatory,
            type: row.mappingStatus === "mapped"
              ? row.mappingType
              : "",
            field: row.mappingStatus === "mapped"
              ? row.mappedField
              : "",
          };
        }
      });

      const response = await fetch("/api/template-setting/mapping/save/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          template_for: context?.is_separate_template
            ? mappingTemplateFor
            : "common",
          template_type: selectedTemplate,
          mapping,
        }),
      });

      const payload = await response.json();

      if (!response.ok || payload.status !== "success") {
        throw new Error(
          payload.message || "Unable to save template mapping."
        );
      }

      await reloadContext(
        selectedTemplate,
        mappingTemplateFor
      );

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: payload.message || "Template mapping saved successfully.",
        confirmButtonText: "OK",
      });

    } catch (requestError) {
      await Swal.fire({
        icon: "error",
        title: "Save Failed",
        text:
          requestError.message ||
          "Unable to save template mapping.",
        confirmButtonText: "OK",
      });

      setError(
        requestError.message ||
        "Unable to save template mapping."
      );

    } finally {
      setSaving(false);
    }
  }

  function openTab(tab) {
    setError("");
    setNotice("");

    if (tab === "mapping" && !hasMappedTemplates) {
      setActiveTab("mapping");
      setNotice("No base template uploaded. Please upload base template first for mapping.");
      return;
    }

    setActiveTab(tab);
  }

  if (!context) {
    return <div className="workspace-panel">Loading template setting…</div>;
  }

  return (
    <section className="template-setting-view">
      

      <div className="workspace-panel">
        <div className="template-tabs">
          <button
            className={`template-tab${activeTab === "upload" ? " is-active" : ""}`}
            type="button"
            onClick={() => openTab("upload")}
          >
            Base Template Upload
          </button>
          <button
            className={`template-tab${activeTab === "mapping" ? " is-active" : ""}`}
            type="button"
            onClick={() => openTab("mapping")}
          >
            Template Header Mapping
          </button>
        </div>

        {activeTab === "upload" ? (
          <div className="template-panel">
            <div className="template-mapping-toolbar">
              <label className="template-mode-option">
                <input
                  type="radio"
                  name="template_split"
                  value="common"
                  checked={templateMode === "common"}
                  onChange={() => {
                    setTemplateMode("common");
                    setFileCount("");
                    setUploadFiles({});
                  }}
                />
                <span>Common Template</span>
              </label>
              <label className="template-mode-option">
                <input
                  type="radio"
                  name="template_split"
                  value="separate"
                  checked={templateMode === "separate"}
                  onChange={() => {
                    setTemplateMode("separate");
                    setFileCount("");
                    setUploadFiles({});
                  }}
                />
                <span>Separate (Material / Service)</span>
              </label>
            </div>

            {templateMode === "separate" ? (
              <div className="template-mapping-toolbar">
                <label className="config-field">
                  <span>Select Template For</span>
                  <select
                    value={uploadTemplateFor}
                    onChange={(event) => {
                      setUploadTemplateFor(event.target.value);
                      setFileCount("");
                      setUploadFiles({});
                    }}
                  >
                    <option value="material">Material</option>
                    <option value="service">Service</option>
                  </select>
                </label>
              </div>
            ) : null}

            <div className="template-mapping-toolbar">
              <label className="config-field">
                <span>Select Number of Files</span>
                <select value={fileCount} onChange={(event) => handleFileCountChange(event.target.value)}>
                  <option value="">--Select--</option>
                  <option value="1">1 File</option>
                  <option value="2">2 Files</option>
                </select>
              </label>
            </div>

            <div className="upload-grid">
              {Object.entries(uploadFiles).map(([key, value], index) => (
                <article key={key} className="upload-card">
                  <div className="upload-card__head">
                    <h3>File {index + 1}</h3>
                    <p>Upload base template and choose header rows.</p>
                  </div>

                  <label className="upload-card__input">
                    <span>Select Excel file</span>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(event) => updateUploadFile(key, "file", event.target.files?.[0] || null)}
                    />
                  </label>

                  <label className="config-field">
                    <span>Header Rows</span>
                    <select
                      value={value.headerRows}
                      onChange={(event) => updateUploadFile(key, "headerRows", event.target.value)}
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                    </select>
                  </label>

                  <div className="upload-card__meta">{value.file ? value.file.name : "No file selected"}</div>
                </article>
              ))}
            </div>

            <div className="template-actions">
              <button className="primary-button" type="button" disabled={uploading} onClick={handleTemplateUpload}>
                {uploading ? "Uploading..." : "Upload Headers"}
              </button>
            </div>

            {uploadStatus ? <div className="config-banner config-banner--success">{uploadStatus}</div> : null}
          </div>
        ) : (
          <div className="template-panel">
            <div className="mapping-note-card">
              <strong>Mode:</strong>
              <p>
                {context.is_separate_template
                  ? "Separate Templates (Material / Service)"
                  : "Common Template"}
              </p>
            </div>

            {context.is_separate_template ? (
              <div className="template-mapping-toolbar">
                <label className="config-field">
                  <span>Select Mapping For</span>
                  <select value={mappingTemplateFor} onChange={(event) => setMappingTemplateFor(event.target.value)}>
                    <option value="material">Material</option>
                    <option value="service">Service</option>
                  </select>
                </label>
              </div>
            ) : null}

            <div className="template-mapping-toolbar">
              <label className="templatesetting-config-field">
                <span>Select Template Header</span>
                <select
                  value={selectedTemplate}
                  disabled={templateOptions.length <= 1}
                  onChange={(event) => setSelectedTemplate(event.target.value)}
                >
                  {templateOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              
            </div>

            {hasMappedTemplates ? (
              <>
              <div className="config-table">
                <table>
                  <thead>
                    <tr>
                      <th className="template-header-name">Header Name</th>
                      <th className="template-mandatory">Mandatory</th>
                      <th className="template-mapping-type">Mapping Type</th>
                      <th className="template-mapped-field">Mapped Field</th>
                    </tr>
                  </thead>

                  <tbody>
                    {mappingRows.map((row, index) => (
                      <tr key={row.headerName}>
                        <td>{row.headerName}</td>

                        <td className="template-mandatory-cell">
                          <input
                            type="checkbox"
                            checked={row.mandatory}
                            onChange={(event) =>
                              updateRow(index, "mandatory", event.target.checked)
                            }
                          />
                        </td>

                        <td>
                          <select
                            value={row.mappingType}
                            onChange={(event) =>
                              updateRow(index, "mappingType", event.target.value)
                            }
                          >
                            <option value="">--Select--</option>
                            <option value="system">System</option>
                            <option value="po">PO</option>
                            <option value="grn">GRN</option>
                          </select>
                        </td>

                        <td>
                          <div className="template-mapped-field-wrapper">
                            <select
                              value={row.mappedField}
                              onChange={(event) =>
                                updateRow(index, "mappedField", event.target.value)
                              }
                            >
                              <option value="">Select Mapping Type</option>

                              {(mappedFieldOptions[row.mappingType] || []).map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>

                            {row.mappingStatus === "mapped" && (
                              <span className="mapping-badge mapping-badge--mapped">
                                ✓ Mapped
                              </span>
                            )}

                            {row.mappingStatus === "suggestion" && (
                              <span
                                className="mapping-badge mapping-badge--suggested mapping-badge--clickable"
                                onClick={() => confirmTemplateSuggestion(index)}
                                title="Click to accept this suggestion"
                              >
                                💡 Suggested
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="template-mapping-submit">
                <button
                  className="primary-button"
                  type="button"
                  disabled={saving}
                  onClick={handleMappingSave}
                >
                  {saving ? "Saving..." : "Submit"}
                </button>
              </div>
              </>
              
            ) : (
              <div className="mapping-note-card">
                <strong>No base template uploaded.</strong>
                <p>Please upload base template first for mapping.</p>
              </div>
            )}

            {mappingStatus ? <div className="config-banner config-banner--success">{mappingStatus}</div> : null}
          </div>
        )}
      </div>
    </section>
  );
}
